import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Zap, Play, Trash2, Plug } from "lucide-react";

export default function Acciones() {
  const [actions, setActions] = useState([]);
  const [form, setForm] = useState({ name: "", webhook_url: "", description: "" });
  const [adding, setAdding] = useState(false);
  const [runId, setRunId] = useState(null);
  const [payload, setPayload] = useState("{}");
  const [result, setResult] = useState(null);

  const load = () => api.get("/actions").then((r) => setActions(r.data.actions || []));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim() || !form.webhook_url.trim()) return toast.error("Nombre y URL son obligatorios");
    try {
      await api.post("/actions", form);
      toast.success("Acción conectada");
      setForm({ name: "", webhook_url: "", description: "" }); setAdding(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Quitar esta acción?")) return;
    await api.delete(`/actions/${id}`); load();
  };

  const run = async (a) => {
    let data = {};
    try { data = payload.trim() ? JSON.parse(payload) : {}; }
    catch { return toast.error("El payload no es un JSON válido"); }
    setResult(null);
    try {
      const { data: res } = await api.post(`/actions/${a.id}/run`, { payload: data });
      setResult({ id: a.id, ok: true, res });
      toast.success("Acción ejecutada");
    } catch (e) {
      setResult({ id: a.id, ok: false, res: e.response?.data?.detail || "Error" });
      toast.error("Falló la acción");
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="acciones-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">AGENTES QUE ACTÚAN</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Acciones</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1 max-w-2xl">
            Conecta tus workflows de n8n. Cada acción es un webhook que la plataforma puede ejecutar para
            <strong> hacer cosas de verdad</strong>: enviar un WhatsApp, mandar un email, agendar una cita…
          </p>
        </div>
        <Button onClick={() => setAdding((v) => !v)}
                className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="ac-add">
          <Plug className="w-4 h-4 mr-2" /> Conectar acción
        </Button>
      </div>

      {adding && (
        <div className="border border-[var(--accent)] bg-[var(--surface)] p-5 space-y-3">
          <div>
            <Label className="label-mono">Nombre de la acción</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Ej. Enviar WhatsApp de confirmación"
                   className="rounded-none bg-[var(--bg)] border-[var(--border)]" data-testid="ac-name" />
          </div>
          <div>
            <Label className="label-mono">URL del webhook de n8n</Label>
            <Input value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                   placeholder="https://tu-n8n/webhook/..."
                   className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs" data-testid="ac-url" />
          </div>
          <div>
            <Label className="label-mono">Descripción (opcional)</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                   placeholder="Qué hace esta acción"
                   className="rounded-none bg-[var(--bg)] border-[var(--border)]" />
          </div>
          <Button onClick={add} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="ac-save">
            Guardar acción
          </Button>
        </div>
      )}

      {actions.length === 0 && !adding ? (
        <div className="border border-dashed border-[var(--border)] p-16 text-center">
          <Zap className="w-10 h-10 mx-auto text-[var(--text-dim)] mb-4" />
          <div className="label-mono mb-2">SIN ACCIONES CONECTADAS</div>
          <p className="text-sm text-[var(--text-dim)] max-w-md mx-auto">
            Conecta el webhook de un workflow de n8n para que tus agentes pasen de aconsejar a actuar.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="border border-[var(--border)] bg-[var(--surface)] p-4">
            <Label className="label-mono">Datos a enviar (JSON, opcional)</Label>
            <Textarea value={payload} onChange={(e) => setPayload(e.target.value)}
                      rows={2} className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs mt-1"
                      placeholder='{"telefono":"600...","mensaje":"..."}' />
          </div>
          {actions.map((a) => (
            <div key={a.id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`ac-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white">{a.name}</div>
                  {a.description && <div className="text-xs text-[var(--text-dim)] mt-0.5">{a.description}</div>}
                  <code className="font-mono text-[10px] text-[var(--text-dim)] break-all">{a.webhook_url}</code>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button onClick={() => run(a)} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 h-8" data-testid={`ac-run-${a.id}`}>
                    <Play className="w-3.5 h-3.5 mr-1" /> Ejecutar
                  </Button>
                  <button onClick={() => del(a.id)} className="text-[var(--text-dim)] hover:text-[var(--danger)]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {result && result.id === a.id && (
                <div className="mt-3 border-l-2 pl-3 text-xs font-mono whitespace-pre-wrap"
                     style={{ borderColor: result.ok ? "var(--accent)" : "var(--danger)", color: "var(--text-dim)" }}>
                  {result.ok ? `✔ ${result.res.status} · ${(result.res.response || "").slice(0, 300)}` : `✗ ${result.res}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
